import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";

import {
  Card,
  Heading,
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  useScrollToViewById
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { AccContractsCard } from "@modules/settings/screens/users/components/user-provider/ AccContactsCard.tsx";
import { BhbProvider } from "@stores/bhb/models/BhbProvider.ts";
import { UserAvailability } from "@stores/booking/models/UserAvailability.ts";
import { User } from "@stores/core/models/User.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { Provider } from "@stores/practice/models/Provider.ts";
import { RootStore } from "@stores/root/RootStore.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";
import { PageLink } from "@ui-components/PageLink/PageLink.tsx";

import { UsersLabels } from "../shared-components/SettingsLabels.ts";
import { UserCommunicationsCard } from "./components/communications/UserCommunicationsCard.tsx";
import { UserContext } from "./components/context/UserContext.tsx";
import { UserHelper } from "./components/context/UserHelper.tsx";
import { ShowUserOnCalendarForm } from "./components/security/ShowUserOnCalendar.tsx";
import { UserAccessForm } from "./components/security/UserAccessForm.tsx";
import { UserSecurityRolesForm } from "./components/security/UserSecurityRolesForm.tsx";
import { UserInformationCard } from "./components/user-info/UserInformationCard.tsx";
import { UserOnlineBookingProfileCard } from "./components/user-online-booking-profile/UserOnlineBookingProfileCard.tsx";
import { UserProviderForm } from "./components/user-provider/UserProviderForm.tsx";
import { UserScreenCardKeys } from "./UserScreen.types.ts";

interface UserScreenProps {
  user: User;
  provider: Provider | undefined;
  pracOrgUnit: PracOrgUnit | undefined;
  bhbProviders: BhbProvider[];
  userAvailability: UserAvailability | undefined;
  lastUserAdmin: boolean;
}

const UserScreenBase: React.FC<UserScreenProps> = observer(
  ({
    user,
    provider,
    pracOrgUnit,
    bhbProviders,
    userAvailability,
    lastUserAdmin
  }) => {
    const root = useStores();
    const { core, routing } = root;
    const locationHash = routing.location.hash.replace("#", "");

    const scrollTo = useScrollToViewById();

    useEffect(() => {
      if (locationHash) scrollTo(locationHash);
    }, [locationHash, scrollTo]);

    const userHelper = useRef<UserHelper>(new UserHelper(root)).current;

    const isAllowedToEdit =
      user.id === core.userId ||
      core.permissions.includes(Permission.UserSettingWrite);

    const messageText = !core.hasPermissions(Permission.SecurityWrite)
      ? "You do not have access to edit this section. Please contact your administrator to update your security settings or business roles"
      : "This is the last user with User Management Administrator security permissions.  Another user must first be granted these permissions";

    return (
      <Stack styles={{ root: { paddingTop: 24 } }} tokens={{ childrenGap: 24 }}>
        <Heading variant="section-heading">{user.fullName}</Heading>
        <UserInformationCard user={user} />
        <UserCommunicationsCard user={user} />
        {user.isProviderClass && provider && (
          <>
            <UserOnlineBookingProfileCard
              bhbProviders={bhbProviders}
              user={user}
              userAvailability={userAvailability}
            />
            <Card
              iconName="work"
              headingLevel="section-heading"
              heading={UsersLabels.professionalCredentials}
              styles={{
                subComponentStyles: {
                  tile: {
                    content: {
                      padding: 0
                    }
                  }
                }
              }}
            >
              <UserProviderForm provider={provider} pracOrgUnit={pracOrgUnit} />
            </Card>
          </>
        )}
        {core.userId === user.id && (
          <Card
            iconName="permissions"
            headingLevel="section-heading"
            heading={UsersLabels.accessSignIn}
            styles={{
              subComponentStyles: {
                tile: {
                  content: {
                    padding: 0
                  }
                }
              }
            }}
          >
            <UserAccessForm />
          </Card>
        )}
        <div>
          <Card
            iconName="ReminderPerson"
            headingLevel="section-heading"
            heading={UsersLabels.userRolesAndSecurityPermissions}
            subHeading={
              !core.hasPermissions(Permission.SecurityWrite) ||
              lastUserAdmin ? (
                <MessageBar
                  styles={{ root: { marginBottom: 5 } }}
                  messageBarType={MessageBarType.info}
                >
                  {messageText}
                </MessageBar>
              ) : null
            }
            styles={{
              subComponentStyles: {
                tile: {
                  content: {
                    padding: 0
                  }
                }
              }
            }}
          >
            <UserContext.Provider value={userHelper}>
              <UserSecurityRolesForm
                user={user}
                lastUserAdmin={lastUserAdmin}
              />
            </UserContext.Provider>
          </Card>
        </div>
        {user.isProviderClass && provider && (
          <AccContractsCard user={user} provider={provider} />
        )}
        {isAllowedToEdit && (
          <DataFetcher
            fetch={async () => {
              return user.id === core.userId
                ? core.hasPermissions([Permission.ClinSettingAllowed])
                : await core.checkUserPermissionsFromAPI(user.username, [
                    Permission.ClinSettingAllowed
                  ]);
            }}
            refetchId={user?.eTag}
          >
            {isClinSettingAllowed =>
              isClinSettingAllowed && (
                <PageLink
                  onClick={() =>
                    routing.push(
                      routes.settings.users.userClinicalView.path({
                        id: user.id
                      })
                    )
                  }
                  headingText={UsersLabels.clinicalView}
                  iconName="ContactCard"
                />
              )
            }
          </DataFetcher>
        )}
        {userAvailability && (
          <Card
            id={UserScreenCardKeys.AppointmentSettings}
            iconName="AddFriend"
            headingLevel="section-heading"
            heading={UsersLabels.apptBook}
            styles={{
              subComponentStyles: {
                tile: {
                  content: {
                    padding: 0
                  }
                }
              }
            }}
          >
            <Stack>
              <ShowUserOnCalendarForm
                user={user}
                userAvailabilityModel={userAvailability}
                bhbProvider={bhbProviders.length ? bhbProviders[0] : undefined}
              />
            </Stack>
          </Card>
        )}
      </Stack>
    );
  }
);

const UserScreenDataFetch: React.FC = () => {
  const getData = async ({
    core,
    practice,
    routing,
    booking,
    userExperience,
    bhb
  }: RootStore): Promise<{
    user: User;
    provider: Provider | undefined;
    pracOrgUnit: PracOrgUnit | undefined;
    bhbProviders: BhbProvider[];
    userAvailability: UserAvailability | undefined;
    lastUserAdmin: boolean;
  }> => {
    const userId = routing.match(routes.settings.users.user)?.params.id;
    if (!userId) {
      throw new Error("No userId");
    }

    const getBhbProviders = () => {
      if (
        !core.hasPermissions([Permission.BhbConfigAllowed, Permission.BhbRead])
      ) {
        return [];
      }

      return bhb.getProvidersForUser(userId).catch(() => []);
    };

    const [user, provider, bhbProviders, userAvailability, pracOrgUnit] =
      await Promise.all([
        core.getUser(userId),
        core.hasPermissions(Permission.ProviderRead)
          ? practice.getProvider(userId, { ignoreCache: true })
          : undefined,
        getBhbProviders(),
        core.hasPermissions(Permission.ProviderRead) &&
        core.hasPermissions(Permission.BookingScheduleRead)
          ? booking.getUserAvailability(userId)
          : undefined,
        core.location.parentOrgUnitId
          ? practice.getOrgUnit(core.location.parentOrgUnitId)
          : undefined,
        userExperience.getUserSetting(userId),
        core.loadAllUsers()
      ]);

    const lastUserAdmin = await core.lastUserAdmin(user);

    return {
      user,
      provider,
      bhbProviders,
      userAvailability,
      lastUserAdmin,
      pracOrgUnit
    };
  };
  return (
    <DataFetcher fetch={getData} fallback={<Spinner />}>
      {({
        user,
        provider,
        pracOrgUnit,
        bhbProviders,
        userAvailability,
        lastUserAdmin
      }) => (
        <UserScreenBase
          user={user}
          provider={provider}
          pracOrgUnit={pracOrgUnit}
          bhbProviders={bhbProviders}
          userAvailability={userAvailability}
          lastUserAdmin={lastUserAdmin}
        />
      )}
    </DataFetcher>
  );
};

const UserScreen = withFetch(
  x => [
    x.practice.ref.accProviderContractTypes.load(),
    x.bhb.ref.areasOfInterest.load()
  ],
  UserScreenDataFetch
);

// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default UserScreen;
